import type {
    PendingReplayMove,
    ReplayEdgeEventDescriptor,
    RuntimeState,
} from '@/workflow-runner/_contract/runtime';

export function settleReplayPosition(args: {
    runtimeState: RuntimeState;
    replayPositionIndex: number;
}): RuntimeState {
    const { runtimeState, replayPositionIndex } = args;

    return {
        ...runtimeState,
        traceSelection: {
            ...runtimeState.traceSelection,
            replayPositionIndex,
        },
        pendingReplayMove: null,
    };
}

export function beginPendingReplayMove(args: {
    runtimeState: RuntimeState;
    direction: PendingReplayMove['direction'];
    fromPositionIndex: number;
    toPositionIndex: number;
    event: ReplayEdgeEventDescriptor;
}): RuntimeState {
    const { runtimeState, direction, fromPositionIndex, toPositionIndex } = args;
    const { eventKind, continuationEventKind } = args.event;
    if (eventKind === 'none') return runtimeState;
    const pendingReplayMove: PendingReplayMove = eventKind === 'primitive-operation'
        ? {
            direction: 'forward',
            fromPositionIndex,
            toPositionIndex,
            eventKind,
            ...(continuationEventKind === undefined ? {} : { continuationEventKind }),
            arithmeticProgress: {
                committedStepIndex: 0,
                pendingStepIndex: 1,
            },
        }
        : {
            direction,
            fromPositionIndex,
            toPositionIndex,
            eventKind,
        };

    return {
        ...runtimeState,
        pendingReplayMove,
    };
}

export function requestNextPrimitiveOperationStep(runtimeState: RuntimeState): RuntimeState {
    const move = runtimeState.pendingReplayMove;
    if (move?.eventKind !== 'primitive-operation') return runtimeState;
    if (move.arithmeticProgress.pendingStepIndex !== null) return runtimeState;
    return {
        ...runtimeState,
        pendingReplayMove: {
            ...move,
            arithmeticProgress: {
                ...move.arithmeticProgress,
                pendingStepIndex: move.arithmeticProgress.committedStepIndex + 1,
            },
        },
    };
}

export function commitPrimitiveOperationStep(args: {
    runtimeState: RuntimeState;
    continuePlayback: boolean;
}): RuntimeState {
    const move = args.runtimeState.pendingReplayMove;
    if (move?.eventKind !== 'primitive-operation') return args.runtimeState;
    const completedStepIndex = move.arithmeticProgress.pendingStepIndex;
    if (completedStepIndex === null) return args.runtimeState;
    return {
        ...args.runtimeState,
        pendingReplayMove: {
            ...move,
            arithmeticProgress: {
                committedStepIndex: completedStepIndex,
                pendingStepIndex: args.continuePlayback ? completedStepIndex + 1 : null,
            },
        },
    };
}

export function retreatPrimitiveOperationStep(runtimeState: RuntimeState): RuntimeState {
    const move = runtimeState.pendingReplayMove;
    if (move?.eventKind !== 'primitive-operation') return runtimeState;
    if (move.arithmeticProgress.pendingStepIndex !== null) return runtimeState;
    if (move.arithmeticProgress.committedStepIndex === 0) {
        return { ...runtimeState, pendingReplayMove: null };
    }
    return {
        ...runtimeState,
        pendingReplayMove: {
            ...move,
            arithmeticProgress: {
                committedStepIndex: move.arithmeticProgress.committedStepIndex - 1,
                pendingStepIndex: null,
            },
        },
    };
}

export function completePrimitiveOperationStep(args: {
    runtimeState: RuntimeState;
    lastStepIndex: number;
    replayPositionCount: number;
    /** Retains manual pauses even when arithmetic precedes a structural continuation. */
    manualInspection?: boolean;
}): RuntimeState {
    const move = args.runtimeState.pendingReplayMove;
    if (move?.eventKind !== 'primitive-operation') return args.runtimeState;
    const completedStepIndex = move.arithmeticProgress.pendingStepIndex;
    if (completedStepIndex === null) return args.runtimeState;
    if (completedStepIndex < args.lastStepIndex) {
        return commitPrimitiveOperationStep({
            runtimeState: args.runtimeState,
            continuePlayback: (move.continuationEventKind !== undefined && !args.manualInspection)
                || (args.runtimeState.playbackAdvanceMode === 'automatic' && args.runtimeState.isPlaying),
        });
    }

    if (move.continuationEventKind !== undefined) {
        return {
            ...args.runtimeState,
            pendingReplayMove: {
                direction: move.direction,
                fromPositionIndex: move.fromPositionIndex,
                toPositionIndex: move.toPositionIndex,
                eventKind: move.continuationEventKind,
                followsPrimitiveOperation: true,
            },
        };
    }

    const settled = settleReplayPosition({
        runtimeState: args.runtimeState,
        replayPositionIndex: move.toPositionIndex,
    });
    return move.toPositionIndex >= args.replayPositionCount
        ? { ...settled, isPlaying: false, accumulatedMs: 0 }
        : settled;
}
